import React, { FC, useCallback, useMemo } from 'react';
import type { GroupBase, StylesConfig } from 'react-select';

import StyledTextWithInfoTooltip from '../../components/display/text/StyledTextWithInfoTooltip';
import SearchableSelect, {
    SearchableSelectType,
} from '../../components/inputs/searchableSelect';

// models
import { AudienceMainModel } from '../../models/audience.model';

// components
import AudienceOnHoverFilters from './AudienceOnHoverFilters';

interface AudienceSelectProps {
    audiences: AudienceMainModel[];
    selected: string;
    handleChange: (value: string) => void;
    menuPortalId?: string;
    label?: string;
    showHoverCard?: boolean;
    placeholder?: string;
    customStyle?: StylesConfig<
        SearchableSelectType,
        boolean,
        GroupBase<SearchableSelectType>
    >;
    isDisabled?: boolean;
}

const AudienceSelect: FC<React.PropsWithChildren<AudienceSelectProps>> = ({
    audiences,
    selected,
    handleChange,
    menuPortalId,
    label,
    showHoverCard = false,
    customStyle,
    placeholder,
    isDisabled,
}) => {
    const options = useMemo<SearchableSelectType[]>(
        () => audiences.map((item) => ({ value: item.id, label: item.name })),

        [audiences],
    );

    const selectionOption: SearchableSelectType | undefined = options.find(
        (item) => item.value === selected,
    );

    const hoveredOptionAudience = useCallback(
        (value: string) => audiences.find((item) => item.id === value),
        [audiences],
    );

    const menuPortal = document.getElementById(menuPortalId!);

    return (
        <>
            {label && (
                <StyledTextWithInfoTooltip
                    text={label}
                    description="Who should we send these messages to?"
                />
            )}
            <SearchableSelect
                name={'Audience-select'}
                options={options}
                value={selectionOption}
                onChange={(field, value) =>
                    handleChange(
                        (value as SearchableSelectType).value as string,
                    )
                }
                customStyles={customStyle}
                menuPortalTarget={menuPortal}
                placeholder={placeholder}
                isDisabled={isDisabled}
                components={{
                    Option: (props) => (
                        <AudienceOnHoverFilters
                            {...props}
                            showHoverCard={showHoverCard}
                            audience={hoveredOptionAudience(
                                props.data.value.toString(),
                            )}
                        />
                    ),
                }}
            />
        </>
    );
};

export default AudienceSelect;
